import { forwardRef } from "react";

// ── Palette (matches the Trishul school logo) ──
const NAVY = "#1E3A6B";           // primary deep navy (from logo)
const NAVY_DEEP = "#132749";      // deeper navy for gridlines / footer
const SKY = "#93C5FD";            // logo light sky blue
const SKY_SOFT = "#e6f1fe";       // very light sky for alt rows
const HEADER = "#dbeafe";         // slightly deeper sky for header band
const ACCENT = "#DC2626";         // logo red accent — reserved for highlights
const LAVENDER = SKY;             // legacy alias — kept minimal churn
const LAVENDER_LIGHT = HEADER;    // legacy alias
const ORANGE = NAVY;              // "orange band" now navy
const GRID = NAVY_DEEP;

const TestTimetablePoster = forwardRef(function TestTimetablePoster({ data }, ref) {
  const {
    profile = {},
    class_name = "",
    sections = "",
    test_name = "PERIODIC TEST",
    test_month_year = "",
    rows = [],
    footer_note = "",
  } = data || {};

  // Show "1 (A&B)", "Play Group", "LKG (A)", etc. — pre-primary stays label-only.
  const _isPP = ["Play Group", "LKG", "UKG"].includes(String(class_name));
  const classLabel = class_name
    ? (_isPP ? String(class_name) : `${String(class_name)}${sections ? ` (${sections})` : ""}`)
    : "";
  const cityBits = [profile.name || "SCHOOL NAME", profile.city || profile.address_city || ""]
    .filter(Boolean).join(", ");

  // Flatten rows for the table renderer, computing rowSpan for the DATE + DAY cells.
  const tableRows = [];
  rows.forEach((r) => {
    const list = (r.sessions && r.sessions.length ? r.sessions : [{ session: "", subject: "", portions: "" }]);
    list.forEach((s, i) => {
      tableRows.push({
        date: r.date || "",
        day: r.day || "",
        span: list.length,
        first: i === 0,
        session: s.session || "",
        subject: s.subject || "",
        portions: s.portions || "",
      });
    });
  });

  // A4 landscape at 96dpi: 1123 × 794 (matches BirthdayPoster + exportPdf).
  return (
    <div
      ref={ref}
      data-testid="test-timetable-poster"
      style={{
        width: 1123,
        height: 794,
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Montserrat', sans-serif",
        color: "#111",
        border: `2px solid ${GRID}`,
        boxSizing: "border-box",
      }}
    >
      {/* ── Header: centred logo + school name + address ── */}
      <div
        style={{
          background: LAVENDER_LIGHT,
          padding: "14px 24px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 18,
          borderBottom: `2px solid ${GRID}`,
          flexShrink: 0,
          textAlign: "center",
        }}
      >
        <div style={{ width: 72, height: 72, background: "#fff", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
          {profile.logo ? (
            <img src={profile.logo} alt="logo" crossOrigin="anonymous" style={{ width: "94%", height: "94%", objectFit: "contain" }} />
          ) : (
            <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 900, color: NAVY, fontSize: 22 }}>SCH</div>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 900, color: NAVY, fontSize: 30, letterSpacing: 0.5, lineHeight: 1.05 }}>
            {profile.name || "School Name"}
          </div>
          {profile.address ? (
            <div style={{ marginTop: 4, fontFamily: "'Montserrat', sans-serif", fontWeight: 600, color: NAVY, fontSize: 14, letterSpacing: 0.3 }}>
              {profile.address}
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Class + title band (orange) ── */}
      <div
        style={{
          background: ORANGE,
          color: "#fff",
          padding: "10px 30px",
          display: "flex",
          alignItems: "center",
          gap: 40,
          fontWeight: 800,
          fontSize: 22,
          letterSpacing: 0.5,
          textShadow: "0 1px 0 rgba(0,0,0,0.10)",
          borderBottom: `1.5px solid ${GRID}`,
          flexShrink: 0,
        }}
      >
        <div>CLASS- {classLabel || "—"}</div>
        <div>TIMETABLE WITH PORTIONS</div>
      </div>

      {/* ── Test name + month/year band (light purple) ── */}
      <div
        style={{
          background: LAVENDER,
          color: NAVY,
          padding: "8px 30px",
          textAlign: "center",
          fontWeight: 800,
          fontSize: 22,
          letterSpacing: 1,
          borderBottom: `1.5px solid ${GRID}`,
          flexShrink: 0,
        }}
      >
        {test_name}{test_month_year ? `      ${test_month_year}` : ""}
      </div>

      {/* ── Table ── */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
            fontSize: 15,
            fontWeight: 700,
            color: "#1a1a1a",
          }}
        >
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "54%" }} />
          </colgroup>
          <thead>
            <tr style={{ background: ORANGE, color: "#fff" }}>
              {["DATE", "DAY", "SESSION", "SUBJECT", "PORTIONS"].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "8px 10px",
                    border: `1.5px solid ${GRID}`,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    fontSize: 15,
                    textAlign: h === "PORTIONS" ? "center" : "center",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 28, textAlign: "center", color: "#666", border: `1px solid ${GRID}` }}>
                  Add test dates and sessions to preview them here.
                </td>
              </tr>
            ) : tableRows.map((r, idx) => {
              const rowBg = (idx % 2 === 0) ? "#ffffff" : SKY_SOFT;
              const cellBase = { padding: "8px 10px", border: `1.2px solid ${GRID}`, background: rowBg, verticalAlign: "middle" };
              return (
                <tr key={idx}>
                  {r.first ? (
                    <td rowSpan={r.span} style={{ ...cellBase, textAlign: "center", fontWeight: 800 }}>{r.date || "—"}</td>
                  ) : null}
                  {r.first ? (
                    <td rowSpan={r.span} style={{ ...cellBase, textAlign: "center", fontWeight: 800 }}>{r.day || "—"}</td>
                  ) : null}
                  <td style={{ ...cellBase, textAlign: "center", fontWeight: 800, letterSpacing: 0.3 }}>{r.session || ""}</td>
                  <td style={{ ...cellBase, textAlign: "center", fontWeight: 800 }}>{r.subject || ""}</td>
                  <td style={{ ...cellBase, textAlign: "left", fontWeight: 600, whiteSpace: "pre-wrap", lineHeight: 1.35 }}>
                    {r.portions || ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Optional footer note ── */}
      {footer_note ? (
        <div
          style={{
            padding: "10px 20px",
            fontSize: 15,
            fontWeight: 700,
            color: "#1a1a1a",
            borderTop: `1.5px solid ${GRID}`,
            background: "#ffffff",
            whiteSpace: "pre-wrap",
            lineHeight: 1.35,
            flexShrink: 0,
          }}
        >
          {footer_note}
        </div>
      ) : null}

      {/* ── School-branded footer strip ── */}
      <div
        style={{
          background: NAVY,
          color: "#dbe6ff",
          textAlign: "center",
          padding: "6px 0",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.3,
          flexShrink: 0,
        }}
      >
        © {profile.name || "School"} &nbsp;|&nbsp; Built by ZeroLabs Digitals
      </div>
    </div>
  );
});

export default TestTimetablePoster;
